/**
 * PageHeader · Wave POS · Mobile overflow fix (P1)
 *
 * Universal page header that prevents horizontal overflow on mobile (375px).
 *
 * Mobile (<sm — 375–639px):
 *   - Title + subtitle stack vertically (wrap naturally, no truncate)
 *   - Badge renders below title
 *   - primaryAction is full-width below the title block
 *   - secondaryActions + overflowActions collapse into a "Mais" button
 *     that opens a MobileDrawer
 *   - filters render in a separate row below the actions row
 *
 * Desktop (≥sm — 640px+):
 *   - Title + badge in one row, secondaryActions inline, primaryAction at end
 *   - overflowActions still in "..." if provided
 *   - filters in a row below (or inline, consumer controls)
 *
 * Touch targets: all buttons ≥ 44px (min-h-[44px]).
 * Safe area: padding-top respects env(safe-area-inset-top) via pb on AppShell.
 * Animations: Emil-style — fade-in-up on mount (30ms stagger on actions).
 */

import { useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/Button';
import { MobileDrawer } from '@/components/ui/MobileDrawer';
import { useT } from '@/hooks/useT';
import { cn } from '@/lib/cn';

// ─── Action types ─────────────────────────────────────────────────────────────

export interface PageHeaderAction {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
  disabled?: boolean;
  loading?: boolean;
  destructive?: boolean;
  /** Hint for screen readers, e.g. "Importar planilha" */
  ariaLabel?: string;
}

// ─── Props ───────────────────────────────────────────────────────────────────

export interface PageHeaderProps {
  /** Main heading — never truncated on mobile; wraps naturally */
  title: string;
  /** Subtitle / description — optional, renders below title */
  subtitle?: string;
  /** Status pill, count badge, or any inline node */
  badge?: ReactNode;
  /** The single most-important action. Full-width on mobile. */
  primaryAction?: PageHeaderAction;
  /**
   * Secondary actions shown inline on desktop (≥sm).
   * On mobile they collapse into the "Mais" drawer.
   */
  secondaryActions?: PageHeaderAction[];
  /**
   * Actions always hidden behind "Mais" on both breakpoints.
   * Use for destructive, rarely-used, or expert actions.
   */
  overflowActions?: PageHeaderAction[];
  /**
   * Search/filter input row. Rendered below the title+actions row
   * on both breakpoints, always full-width.
   */
  filters?: ReactNode;
  /** Extra class on the outermost container */
  className?: string;
  /** data-testid for the root element */
  'data-testid'?: string;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function PageHeader({
  title,
  subtitle,
  badge,
  primaryAction,
  secondaryActions = [],
  overflowActions = [],
  filters,
  className,
  'data-testid': testId,
}: PageHeaderProps) {
  const { t } = useT();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Actions that go into the "Mais" drawer on mobile
  const mobileDrawerActions = [...secondaryActions, ...overflowActions];
  const hasOverflow = mobileDrawerActions.length > 0;

  // On desktop, secondary actions are inline. Overflow actions go into "Mais".
  const hasDesktopMore = overflowActions.length > 0;

  return (
    <header
      className={cn('animate-fade-in-up', className)}
      data-testid={testId ?? 'page-header'}
    >
      {/* ── Row 1: title + badge + actions ─────────────────────────── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">

        {/* Left: title block */}
        <div className="min-w-0 flex-1">
          {/* Title — never whitespace-nowrap, wraps on narrow viewport */}
          <h1 className="text-xl font-semibold tracking-tight text-ink sm:text-2xl">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-0.5 text-sm text-ink-muted">
              {subtitle}
            </p>
          )}
          {badge && (
            <div className="mt-1.5">
              {badge}
            </div>
          )}
        </div>

        {/* Right: actions */}
        <div className="flex shrink-0 items-center gap-2">

          {/* ── Desktop: secondary actions inline ── */}
          {secondaryActions.length > 0 && (
            <div className="hidden sm:flex sm:items-center sm:gap-2">
              {secondaryActions.map((action, i) => (
                <Button
                  key={i}
                  variant="secondary"
                  size="sm"
                  onClick={action.onClick}
                  disabled={action.disabled}
                  aria-label={action.ariaLabel ?? action.label}
                >
                  {action.icon && <span aria-hidden>{action.icon}</span>}
                  {action.label}
                </Button>
              ))}
            </div>
          )}

          {/* ── Desktop: overflow "Mais" ── */}
          {hasDesktopMore && (
            <div className="hidden sm:block">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDrawerOpen(true)}
                aria-label={t.pageHeader.moreActionsAriaLabel}
                data-testid="page-header-more-desktop"
              >
                {t.pageHeader.more}
                <svg
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  width={14}
                  height={14}
                  aria-hidden
                  className="ml-1"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                    clipRule="evenodd"
                  />
                </svg>
              </Button>
            </div>
          )}

          {/* ── Mobile: "Mais" collapses secondary + overflow ── */}
          {hasOverflow && (
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              aria-label={t.pageHeader.moreActionsAriaLabel}
              data-testid="page-header-more-mobile"
              className={cn(
                'sm:hidden',
                'inline-flex min-h-[44px] min-w-[44px] items-center justify-center',
                'rounded-xl border border-line bg-surface-2',
                'text-ink-muted hover:bg-surface-3 hover:text-ink',
                'transition-colors duration-160',
                'active:scale-[0.97] transition-transform',
              )}
            >
              <EllipsisIcon />
              <span className="sr-only">{t.pageHeader.more}</span>
            </button>
          )}

          {/* ── Primary action ── */}
          {primaryAction && (
            <Button
              variant="primary"
              size="sm"
              onClick={primaryAction.onClick}
              disabled={primaryAction.disabled}
              aria-label={primaryAction.ariaLabel ?? primaryAction.label}
              data-testid="page-header-primary-action"
              className="min-h-[44px] sm:min-h-0"
            >
              {primaryAction.icon && <span aria-hidden>{primaryAction.icon}</span>}
              {primaryAction.label}
            </Button>
          )}
        </div>
      </div>

      {/* ── Row 2: filters (always full-width, separate row) ─────── */}
      {filters && (
        <div className="mt-3" data-testid="page-header-filters">
          {filters}
        </div>
      )}

      {/* ── Mobile drawer: secondary + overflow actions ─────────── */}
      <MobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={t.pageHeader.moreActionsAriaLabel}
        aria-label={t.pageHeader.moreActionsAriaLabel}
      >
        <div className="flex flex-col gap-2" data-testid="page-header-drawer-actions">
          {mobileDrawerActions.map((action, i) => (
            <button
              key={i}
              type="button"
              onClick={() => {
                setDrawerOpen(false);
                action.onClick();
              }}
              disabled={action.disabled}
              aria-label={action.ariaLabel ?? action.label}
              className={cn(
                'flex min-h-[44px] w-full items-center gap-3 rounded-xl px-4 py-3',
                'text-left text-sm font-medium',
                'transition-colors duration-160',
                action.destructive
                  ? 'text-err hover:bg-err/10'
                  : 'text-ink hover:bg-surface-3',
                action.disabled && 'pointer-events-none opacity-50',
              )}
            >
              {action.icon && (
                <span className="flex h-5 w-5 shrink-0 items-center justify-center" aria-hidden>
                  {action.icon}
                </span>
              )}
              {action.label}
            </button>
          ))}
        </div>
      </MobileDrawer>
    </header>
  );
}

// ─── Icons ───────────────────────────────────────────────────────────────────

function EllipsisIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      width={20}
      height={20}
      aria-hidden
    >
      <circle cx="5" cy="12" r="1.5" />
      <circle cx="12" cy="12" r="1.5" />
      <circle cx="19" cy="12" r="1.5" />
    </svg>
  );
}
